/**
 * Montar la tienda de práctica.
 *
 * Split out of `practica.ts` because `dexie.ts` has to ask that module for the database name
 * while it is constructing the connection — so anything `practica.ts` imports would be pulled
 * in ahead of the database itself, and importing the database from it would close the circle.
 * Keeping the flag in a module that imports nothing lets this one import everything normally.
 */

import { dolaresEsperados, efectivoEsperado } from '@abastece/core';
import { hashPin, newId } from '@abastece/shared';
import { db, setMeta } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { traducir } from '../i18n/index.js';
import { sesionAbierta } from './caja.js';
import { cargarDemo } from './demo.js';
import { emitir } from './emitir.js';
import { aMedioTurno } from './practica.js';

/** Fixed, so every practice ticket reads `PR-000001` and is obviously not a real receipt. */
const CODIGO_PRACTICA = 'PR';

/**
 * The session is restored from meta, not from a PIN pad, so this is typed only where the app asks
 * for the signed-in user's PIN again: voiding a sale. The lesson on voids says what it is.
 */
const PIN_PRACTICA = '000000';

let montaje: Promise<void> | null = null;

/**
 * Stand up the sandbox shop, entirely offline.
 *
 * The real app gets its device code and its owner from the server, because in a real shop the
 * code is what stops two terminals numbering the same receipt. A practice shop has exactly one
 * terminal and talks to nobody, so it issues its own.
 *
 * Shared-promise guard for the same reason as enrolment: `StrictMode` invokes the mount effect
 * twice, both calls saw an empty shop, and both seeded it — which is why a practice shop came
 * up with two of every order and two pending transfers.
 */
export function prepararPractica(avisar: (paso: string) => void = () => {}): Promise<void> {
  montaje ??= montar(avisar).finally(() => {
    montaje = null;
  });
  return montaje;
}

/**
 * The three meta rows go in BEFORE the two events, because `emitir` reads the device and the
 * actor out of meta to stamp the envelope — and the events being stamped are the ones that
 * create that very device and that very actor. The server's own bootstrap has the same shape,
 * and for the same reason: something has to be the first fact.
 */
async function montar(avisar: (paso: string) => void): Promise<void> {
  if ((await db.usuarios.count()) > 0) return;

  const usuarioId = newId();
  const dispositivoId = newId();

  await setMeta('dispositivoId', dispositivoId);
  await setMeta('dispositivoCodigo', CODIGO_PRACTICA);
  await setMeta('usuarioActivoId', usuarioId);
  await setMeta('proximoDeviceSeq', 1);
  await setMeta('ultimoServerSeq', 0);

  avisar(traducir('practica.montando'));
  await emitir('usuario.creado', {
    usuarioId,
    nombre: traducir('practica.usuario'),
    // The owner's role, so every lesson is reachable. Which lessons a person is SHOWN is
    // decided by the role they have in the real shop, not by this one.
    rol: 'dueno',
    pinHash: await hashPin(PIN_PRACTICA),
  });
  await emitir('dispositivo.registrado', {
    dispositivoId,
    codigo: CODIGO_PRACTICA,
    nombre: traducir('practica.dispositivo'),
    tipo: 'mostrador',
  });

  await cargarDemo(avisar);
  // The learner's day starts where the lessons do, with the till to open: the morning's shift,
  // which rang up the seed's sales, has been counted and closed, square, its float left for the
  // next one. Opened by the seed and left open, the second lesson told somebody the till «está
  // cerrada» over a till that was open, and had nothing for them to open.
  if (!aMedioTurno()) await cerrarElTurnoDeLaManana(usuarioId);
}

/** The corte the owner would make of the seed's shift: exactly what it expects, received in hand. */
async function cerrarElTurnoDeLaManana(duenoId: string): Promise<void> {
  const sesion = await sesionAbierta();
  if (!sesion) return;
  const config = await readSettings();
  await emitir('caja.cerrada', {
    sesionId: sesion.id,
    conteoCordobas: efectivoEsperado(sesion),
    conteoDolares: dolaresEsperados(sesion),
    tasaCambio: config.fxTasaOficial,
    // A float left on purpose, though the corte now suggests none: the lessons and the till's
    // scripts count the afternoon against one.
    queda: config.cajaFondo ?? sesion.fondoInicial,
    recibidoPor: duenoId,
  });
}
